import React, { memo } from 'react';
import { Box, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { LIST_TABLE_SX } from '../../mathReports/mathTableStyles';
import { coverageOf } from './revenueCalc';
import { plural } from './revenueOps';
import type { RevenueColumn } from './revenueApi';

const PROTECTED_HINT = 'Служебная колонка: на ней держатся отчеты аптек, удалить ее нельзя';
const PARTIAL_HINT = 'Колонки нет в части таблиц аптек: отчеты этих аптек не откроются, пока она не будет добавлена';
const BUSY_HINT = 'Идет обслуживание таблиц выручки, дождитесь окончания операции';

function deleteHint(item: RevenueColumn, busy: boolean): string {
  if (item.isProtected) return PROTECTED_HINT;
  if (item.usedIn.length > 0) return `Колонка входит в формулы: ${item.usedIn.join(', ')}. Сначала уберите ее из формул`;
  if (busy) return BUSY_HINT;
  return 'Удалить колонку';
}

/** Подсказка кнопки добавления колонки в недостающие таблицы аптек */
function syncHint(missing: number, busy: boolean): string {
  if (busy) return BUSY_HINT;
  return `Добавить колонку в ${missing} ${plural(missing, 'таблицу', 'таблицы', 'таблиц')} аптек, где ее нет. На это время работа с данными аптек приостановится`;
}

interface RevenueRowProps {
  item: RevenueColumn;
  pharmacyTables: number | null;
  canEdit: boolean;
  canDelete: boolean;
  canSync: boolean;
  busy: boolean;
  onEdit: (item: RevenueColumn) => void;
  onDelete: (item: RevenueColumn) => void;
  onSync: (item: RevenueColumn) => void;
}

const RevenueRow = memo(({ item, pharmacyTables, canEdit, canDelete, canSync, busy, onEdit, onDelete, onSync }: RevenueRowProps) => {
  const coverage = coverageOf(item, pharmacyTables);
  const formula = (item.formula || '').trim();
  const blocked = item.isProtected || item.usedIn.length > 0 || busy;
  const missing = item.inTables !== null && pharmacyTables !== null ? pharmacyTables - item.inTables : 0;

  return (
    <tr>
      <td className="muted">{item.id}</td>
      <td>
        <code>{item.cName.trim()}</code>
      </td>
      <td className="strong">{item.displayName.trim()}</td>
      <td className="muted">{item.cType.trim()}</td>
      <td className="muted">{item.sortByGroup || '-'}</td>
      <td className="center">{item.important === 1 ? <span className="tag on">Да</span> : <span className="muted">-</span>}</td>
      <td className="center">
        {formula ? (
          <Tooltip title={`Формула: ${formula}`} enterDelay={300} arrow>
            <span className="tag on">Формула</span>
          </Tooltip>
        ) : item.InputBlock === 1 ? (
          <span className="tag">Закрыт</span>
        ) : (
          <span className="muted">Открыт</span>
        )}
      </td>
      <td className="center">
        {item.statisticsHide === 1 || item.hideInStatTable === 1 ? (
          <Tooltip
            title={[item.statisticsHide === 1 ? 'Скрыта в итогах' : '', item.hideInStatTable === 1 ? 'Не показывается в статистике' : '']
              .filter(Boolean)
              .join('. ')}
            enterDelay={300}
            arrow
          >
            <span className="tag">Скрыта</span>
          </Tooltip>
        ) : (
          <span className="muted">-</span>
        )}
      </td>
      <td className="center nowrap">
        {coverage === 'unknown' ? (
          <span className="muted">-</span>
        ) : coverage === 'full' ? (
          <span className="muted">
            {item.inTables} / {pharmacyTables}
          </span>
        ) : (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={PARTIAL_HINT} enterDelay={300} arrow>
              <span style={{ color: '#b45309', fontWeight: 600 }}>
                {item.inTables} / {pharmacyTables}
              </span>
            </Tooltip>
            {canSync && missing > 0 && (
              <Tooltip title={syncHint(missing, busy)} enterDelay={300} arrow>
                {/* span нужен, чтобы подсказка работала и на отключенной кнопке */}
                <span>
                  <button type="button" className="act" disabled={busy} onClick={() => onSync(item)}>
                    Добавить в недостающие
                  </button>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </td>
      {(canEdit || canDelete) && (
        <td className="right nowrap">
          {canEdit && (
            <Tooltip title="Изменить параметры колонки" enterDelay={800} arrow>
              <button type="button" className="act icon" aria-label="Изменить колонку" onClick={() => onEdit(item)}>
                <EditOutlinedIcon />
              </button>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title={deleteHint(item, busy)} enterDelay={blocked ? 200 : 800} arrow>
              {/* span нужен, чтобы подсказка работала и на отключенной кнопке */}
              <span>
                <button
                  type="button"
                  className="act icon danger"
                  aria-label="Удалить колонку"
                  disabled={blocked}
                  onClick={() => onDelete(item)}
                >
                  <DeleteOutlineIcon />
                </button>
              </span>
            </Tooltip>
          )}
        </td>
      )}
    </tr>
  );
});

interface RevenueColumnsTableProps {
  columns: RevenueColumn[];
  pharmacyTables: number | null;
  canEdit: boolean;
  canDelete: boolean;
  /** Может добавлять колонку в недостающие таблицы аптек (tables.revenue.sync.run) */
  canSync: boolean;
  /** Идет операция над таблицами аптек: изменения колонок сейчас не принимаются */
  busy: boolean;
  onEdit: (item: RevenueColumn) => void;
  onDelete: (item: RevenueColumn) => void;
  onSync: (item: RevenueColumn) => void;
}

const RevenueColumnsTable: React.FC<RevenueColumnsTableProps> = ({
  columns,
  pharmacyTables,
  canEdit,
  canDelete,
  canSync,
  busy,
  onEdit,
  onDelete,
  onSync
}) => (
  <Box sx={LIST_TABLE_SX}>
    <table>
      <thead>
        <tr>
          <th className="narrow">ID</th>
          <th>Имя в БД</th>
          <th>Название</th>
          <th>Тип</th>
          <th>Группа</th>
          <th className="center">Обязательная</th>
          <th className="center">Ввод</th>
          <th className="center">Скрытие</th>
          <th className="center">В таблицах аптек</th>
          {(canEdit || canDelete) && <th className="right actions">Действия</th>}
        </tr>
      </thead>
      <tbody>
        {columns.map((item) => (
          <RevenueRow
            key={item.id}
            item={item}
            pharmacyTables={pharmacyTables}
            canEdit={canEdit}
            canDelete={canDelete}
            canSync={canSync}
            busy={busy}
            onEdit={onEdit}
            onDelete={onDelete}
            onSync={onSync}
          />
        ))}
      </tbody>
    </table>
  </Box>
);

export default memo(RevenueColumnsTable);
